// Which lighting sliders can change a template's image.
//
// The Shadows and Reflections / highlights sliders only scale the baked
// lighting of clipped layers above the design (cpuRenderer.js / renderer.js):
//   shadow    = max(0, 1 - gain - bias)   scaled by the Shadows slider
//   highlight = bias                      scaled by the Highlights slider
// A PSD without shading layers leaves gain = 1 and bias = 0, so the sliders
// would do nothing. This reads the same baked data, so it works for every
// template (stored, backfilled or converted in the browser) without a change
// to the template format.

/** Per-channel change (0..1) below which a pixel counts as unaffected (byte rounding). */
const THRESHOLD = 3 / 255;
/** Share of the design surface that must be affected for a slider to be useful. */
const MIN_SHARE = 0.005;

const cache = new WeakMap();

/**
 * Returns { shadows, highlights }: whether each slider visibly changes the
 * mockup. Unknown (no template yet) counts as available.
 */
export function lightingAvailability(template, { threshold = THRESHOLD, minShare = MIN_SHARE } = {}) {
  if (!template?.coverage || !template.lightGain || !template.lightBias) return { shadows: true, highlights: true };
  const cached = cache.get(template);
  if (cached && cached.threshold === threshold && cached.minShare === minShare) return cached.result;

  const { coverage, lightGain, lightBias, lightBiasSigned } = template;
  let covered = 0, shadowPixels = 0, highlightPixels = 0;
  for (let p = 0; p < coverage.length; p++) {
    if (!coverage[p]) continue;
    covered++;
    let shadow = false, highlight = false;
    for (let c = 0, q = p * 4; c < 3; c++, q++) {
      const gain = lightGain[q] / 255;
      const bias = lightBiasSigned ? (lightBias[q] / 255) * 2 - 1 : lightBias[q] / 255;
      if (1 - gain - bias > threshold) shadow = true;
      if (Math.abs(bias) > threshold) highlight = true;
    }
    if (shadow) shadowPixels++;
    if (highlight) highlightPixels++;
  }
  const needed = Math.max(1, Math.ceil(covered * minShare));
  const result = { shadows: covered > 0 && shadowPixels >= needed, highlights: covered > 0 && highlightPixels >= needed };
  cache.set(template, { threshold, minShare, result });
  return result;
}
